/**
 * « Nouvelle transaction » du test — refaite CHAMP POUR CHAMP sur celle du
 * poste (`panels/fan/TransactionComposerModal.tsx`), telle qu'elle est depuis
 * le 13-09-2026.
 *
 * 🩸 Elle était en RETARD d'un mois (constaté le 06-10) : légende, trombone du
 * teaser, aperçu du bouton « Débloquer pour X € ». Le vrai composeur a tout
 * perdu le 13-09 — Kayla : « on envoie juste le lien de paiement. Que ce soit
 * hyper simple : ils choisissent le produit, ils mettent le prix, ils
 * choisissent le contenu à délivrer, et il envoie. » Un candidat qui apprend
 * ici à remplir une légende apprend un geste qui n'existe plus.
 *
 * Champs, dans l'ordre du poste, et rien d'autre :
 *   1. PRODUIT — choisi dans le catalogue, jamais tapé (`ChoixDuProduit`). Le
 *      champ Prix n'est PAS pré-rempli : on n'a pas relevé le prix de base de
 *      ces produits, et c'est au candidat de mettre le prix CONCLU ;
 *   2. PRIX + DEVISE (EUR / USD — les deux seules de Dropp) ;
 *   3. « Il a déjà payé » — le lien discret qui ENREGISTRE une vente sans rien
 *      envoyer. Il est là parce qu'il est au poste : le cocher ici est une
 *      erreur que la note relève ;
 *   4. DÉLIVRER AU PAIEMENT (OPTIONNEL) — c'est ICI qu'on choisit LE média
 *      vendu (06-10-2026 : on vend un média, pas une formule). Optionnel au
 *      poste ; noté ici (`tx_media`) : un fan qui paie la vidéo doit la recevoir ;
 *   5. le récapitulatif : « le fan reçoit le lien, et rien d'autre ».
 *
 * ⛔ POURQUOI CE N'EST PAS LE VRAI COMPOSANT : il va chercher le catalogue et
 * le Vault par des requêtes AUTHENTIFIÉES (`useOfferCatalog`, `VaultPicker`).
 * Un candidat n'a pas de compte, et on ne montre pas le Vault de Kayla à un
 * inconnu. Rien ne part d'ici : `onEnvoyer` rend la main au moteur du test.
 */
import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Carte,
  Checkbox,
  Etiquette,
  Grille,
  Ligne,
  Lignes,
  Modal,
  ModalBody,
  ModalFooter,
  Saisie,
  Vide,
} from "@/shared/ui";
import { Plus, X } from "@/shared/ui/icons";

import { POSTE_THEME } from "../PhoneFrame";
import { FicheDuMedia } from "./PanneauDeDroite";
import { CATALOGUE, VAULT, mediaParId, offreParCle, type OffreCatalogue } from "./scenario";

/** Dropp ne connaît que ces deux-là (cf. le vrai composeur). */
const DEVISES = ["EUR", "USD"] as const;

export interface EnvoiTransaction {
  /** Clé du produit choisi — null s'il n'en a pas choisi (le vrai bouton reste
   *  alors désarmé ; ce cas n'arrive donc pas d'ici, mais le moteur le sait). */
  offreCle: string | null;
  prixCents: number;
  devise: string;
  /** Les médias du Vault délivrés au paiement. */
  medias: string[];
  dejaPaye: boolean;
}

/** Ce que « Préparer la vente » (Francis) ou un bouton de transaction d'un
 *  script pose dans la fenêtre à l'ouverture. */
export interface PreRemplissage {
  offreCle: string | null;
  prixCents: number;
  mediaId: string | null;
}

interface Props {
  ouvert: boolean;
  prerempli?: PreRemplissage | null;
  onFermer: () => void;
  onEnvoyer: (t: EnvoiTransaction) => void;
}

function enLettres(cents: number): string {
  return (cents / 100).toString().replace(".", ",");
}

export function TransactionModale({ ouvert, prerempli = null, onFermer, onEnvoyer }: Props) {
  const [offre, setOffre] = useState<OffreCatalogue | null>(null);
  /** Le prix conclu, en CENTIMES (`ChampMontant`) — `null` tant qu'il n'a rien écrit. */
  const [prix, setPrix] = useState<number | null>(null);
  const [devise, setDevise] = useState<string>("EUR");
  const [dejaPaye, setDejaPaye] = useState(false);
  const [livraison, setLivraison] = useState<string[]>([]);
  const [choixOuvert, setChoixOuvert] = useState(false);

  // Chaque ouverture repart de zéro — ou de ce que Francis a préparé. « Ça
  // pré-remplit, il valide » (Kayla, 20-08) : rien ne part sans son clic.
  useEffect(() => {
    if (!ouvert) return;
    const o = prerempli ? offreParCle(prerempli.offreCle) : null;
    setOffre(o);
    setPrix(prerempli ? prerempli.prixCents : null);
    setDevise("EUR");
    setDejaPaye(false);
    setLivraison(prerempli?.mediaId ? [prerempli.mediaId] : []);
    setChoixOuvert(Boolean(prerempli?.mediaId));
  }, [ouvert, prerempli]);

  const prixCents = prix ?? 0;
  const validation = !offre
    ? "Choisis le produit dans le catalogue."
    : prixCents <= 0
      ? "Prix invalide (nombre positif requis)."
      : null;
  const prixLu = prixCents > 0 ? `${enLettres(prixCents)} ${devise}` : "—";

  function envoyer() {
    if (validation) return;
    onEnvoyer({
      offreCle: offre?.cle ?? null,
      prixCents,
      devise,
      medias: dejaPaye ? [] : livraison,
      dejaPaye,
    });
  }

  return (
    <Modal
      isOpen={ouvert}
      onClose={onFermer}
      size="md"
      scope="viewport"
      title={dejaPaye ? "Confirmer un paiement" : "Nouvelle transaction"}
      containerStyle={POSTE_THEME}
    >
      <ModalBody>
        <div className="flex flex-col gap-4">
          <ChoixDuProduit offre={offre} onChoisir={setOffre} />

          {/* Le prix et sa devise sont UN champ : la devise est le bouton au bout (EUR / USD,
              les deux seules de Dropp). La valeur est en centimes — c'est ce que `onEnvoyer` porte. */}
          <Saisie.ChampMontant
            etiquette="Prix"
            centimes={prix}
            onChange={setPrix}
            devise={devise}
            devises={DEVISES}
            onDevise={setDevise}
            placeholder="150"
          />

          <Button
            variant="ghost"
            size="lg"
            className="-ml-3 h-auto min-h-10 justify-start self-start whitespace-normal px-3 py-2 text-left text-[13px]"
            onClick={() => setDejaPaye((v) => !v)}
          >
            {dejaPaye
              ? "Envoyer un lien de paiement plutôt"
              : "Il a déjà payé — enregistrer la vente sans rien envoyer"}
          </Button>

          {!dejaPaye && (
            <>
              <div className="flex flex-col gap-2">
                <Etiquette compte={livraison.length > 0 ? livraison.length : undefined}>
                  Délivrer au paiement (optionnel)
                </Etiquette>
                <p className="text-[13px] text-muted">Envoyé automatiquement au fan dès qu'il paie.</p>
                {choixOuvert ? (
                  <Grille colonnes={2} ecart={8}>
                    {VAULT.map((m) => {
                      const actif = livraison.includes(m.id);
                      return (
                        <Carte
                          key={m.id}
                          remplissage="sm"
                          actif={actif}
                          aria-pressed={actif}
                          onClick={() =>
                            setLivraison((s) => (s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id]))
                          }
                          className="flex items-center gap-2.5"
                        >
                          <span className="shrink-0">
                            <Checkbox checked={actif} readOnly size="sm" ariaLabel={m.titre} />
                          </span>
                          <span className="flex min-w-0 flex-col gap-0.5">
                            <Etiquette>{m.duree}</Etiquette>
                            <span className="text-[14px] leading-snug text-fg">{m.titre}</span>
                          </span>
                        </Carte>
                      );
                    })}
                  </Grille>
                ) : null}
                {/* La fiche de ce qu'il délivre — relire ce qu'on vend avant
                    d'envoyer. */}
                {livraison.map((id) => {
                  const m = mediaParId(id);
                  return m ? <FicheDuMedia key={id} fiche={m} compacte /> : null;
                })}
                {!choixOuvert && (
                  <Button
                    variant="secondary"
                    size="lg"
                    className="min-h-10 self-start text-[13px]"
                    icon={<Plus className="h-4 w-4" />}
                    onClick={() => setChoixOuvert(true)}
                  >
                    {livraison.length > 0 ? "Ajouter / modifier" : "Choisir le contenu à délivrer"}
                  </Button>
                )}
              </div>

              <p className="text-[13px] leading-relaxed text-muted">
                Le fan reçoit le lien de paiement à {prixLu}, et rien d'autre. Le contenu choisi
                part tout seul dès qu'il paie.
              </p>
            </>
          )}

          {dejaPaye && (
            <p className="text-[13px] leading-relaxed text-muted">
              Enregistre une vente de {prixLu} : rien n'est envoyé au fan.
            </p>
          )}
        </div>
      </ModalBody>

      {/* Étroit : le statut sur sa ligne, les deux boutons dessous à pleine largeur (le texte ne
          se plie plus en deux à côté des boutons) ; à partir de 460 px, tout sur une ligne. */}
      <ModalFooter className="flex-wrap justify-between gap-y-2">
        <span className="min-w-0 basis-full text-[13px] text-muted min-[460px]:basis-auto">
          {validation ?? (dejaPaye ? "Prêt à enregistrer" : "Prêt à envoyer")}
        </span>
        <span className="flex w-full shrink-0 items-center gap-2 min-[460px]:w-auto">
          <Button variant="secondary" size="lg" className="min-h-10 flex-none" onClick={onFermer}>
            Annuler
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="min-h-10 flex-1 whitespace-nowrap min-[460px]:flex-none"
            disabled={validation !== null}
            onClick={envoyer}
          >
            {dejaPaye ? "Confirmer le paiement" : "Envoyer"}
          </Button>
        </span>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Le produit — la recherche et la liste du poste (`ChoixDuProduit`), réduites
 * au sous-ensemble du catalogue du test. Choisi, il devient une carte avec sa
 * fourchette et « Changer de produit ».
 */
function ChoixDuProduit({
  offre,
  onChoisir,
}: {
  offre: OffreCatalogue | null;
  onChoisir: (o: OffreCatalogue | null) => void;
}) {
  const [recherche, setRecherche] = useState("");
  const resultats = useMemo(() => Saisie.filtrerParRecherche(CATALOGUE, recherche, (o) => [o.nom]), [recherche]);

  if (offre) {
    return (
      <div className="flex flex-col gap-1.5">
        <Etiquette>Produit</Etiquette>
        <Carte remplissage="sm" className="flex flex-wrap items-center gap-3">
          <div className="min-w-[9rem] flex-1">
            <div className="break-words text-[16px] font-semibold leading-snug text-fg">{offre.nom}</div>
            <div className="text-[13px] text-muted">Fourchette : {offre.prixLabel}</div>
          </div>
          <Button
            variant="secondary"
            size="lg"
            className="min-h-10 shrink-0"
            onClick={() => onChoisir(null)}
            icon={<X className="h-4 w-4" />}
          >
            Changer de produit
          </Button>
        </Carte>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Saisie.Recherche
        etiquette="Produit"
        valeur={recherche}
        onChange={setRecherche}
        placeholder="Chercher dans le catalogue…"
      />
      <Carte remplissage="aucun" className="max-h-72 overflow-y-auto">
        {resultats.length === 0 ? (
          <Vide taille="sm" titre="Aucun produit ne correspond." />
        ) : (
          <Lignes ariaLabel="Catalogue">
            {resultats.map((o) => (
              <Ligne
                key={o.cle}
                etiquette={o.nom}
                valeur={o.prixLabel}
                onClick={() => {
                  setRecherche("");
                  onChoisir(o);
                }}
              />
            ))}
          </Lignes>
        )}
      </Carte>
    </div>
  );
}
